import React, { useState, useEffect } from 'react';
import { Sidebar } from '@/components/Sidebar';
import { SubscriptionCard } from '@/components/SubscriptionCard';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { ClienteFormDialog } from '@/components/ClienteFormDialog';
import { cn } from '@/lib/utils';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Filter, CheckCircle, XCircle, AlertCircle, CopyPlus, Check, ChevronsUpDown, Paperclip, Eye, X, Loader2, FileText, RotateCcw } from 'lucide-react';

const getClienteDisplayName = (cliente) =>
  cliente.tipo === 'PF' ? cliente.nome : (cliente.nome_fantasia || cliente.razao_social || cliente.nome);

const addMonths = (dateStr, months) => {
  const [year, month, day] = dateStr.split('-').map(Number);
  const targetMonth = month - 1 + months;
  const targetYear = year + Math.floor(targetMonth / 12);
  const normalizedMonth = targetMonth % 12;
  const lastDay = new Date(targetYear, normalizedMonth + 1, 0).getDate();
  const clampedDay = Math.min(day, lastDay);
  return `${targetYear}-${String(normalizedMonth + 1).padStart(2, '0')}-${String(clampedDay).padStart(2, '0')}`;
};

// Função para formatar valor como moeda brasileira
const formatCurrencyInput = (value) => {
  // Remove tudo que não é número
  let numbers = value.replace(/\D/g, '');
  
  // Se vazio, retorna vazio
  if (!numbers) return '';
  
  // Converte para número e divide por 100 para ter os centavos
  let numValue = parseInt(numbers, 10) / 100;
  
  // Formata como moeda brasileira
  return numValue.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

// Função para converter valor formatado para número
const parseCurrencyToNumber = (value) => {
  if (!value) return 0;
  // Remove pontos de milhar e troca vírgula por ponto
  let cleaned = value.replace(/\./g, '').replace(',', '.');
  return parseFloat(cleaned) || 0;
};

const ContasReceber = ({ user, onLogout }) => {
  const [contas, setContas] = useState([]);
  const [filteredContas, setFilteredContas] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [clienteComboOpen, setClienteComboOpen] = useState(false);
  const [clienteSearch, setClienteSearch] = useState('');
  const [showNovoClienteDialog, setShowNovoClienteDialog] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [editingConta, setEditingConta] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategoria, setFilterCategoria] = useState('all');
  const [filterCliente, setFilterCliente] = useState('all');
  const [loading, setLoading] = useState(false);
  const [selectedContas, setSelectedContas] = useState([]);
  const [bancosAtivos, setBancosAtivos] = useState([]);
  const [contaParaReceber, setContaParaReceber] = useState(null);
  const [bancoSelecionadoRecebimento, setBancoSelecionadoRecebimento] = useState('');
  const [repetirConta, setRepetirConta] = useState(false);
  const [numRepeticoes, setNumRepeticoes] = useState(1);
  const [documentos, setDocumentos] = useState([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [contadoresNota, setContadoresNota] = useState([]);
  const [enviarContador, setEnviarContador] = useState(false);
  const [notaUnidade, setNotaUnidade] = useState('');
  const [notaContadorId, setNotaContadorId] = useState('');
  const [notaVinculadaExistente, setNotaVinculadaExistente] = useState(null);

  const [formData, setFormData] = useState({
    descricao: '',
    categoria: '',
    data_emissao: new Date().toISOString().slice(0, 10),
    data_vencimento: new Date().toISOString().slice(0, 10),
    valor: '',
    forma_pagamento: 'PIX',
    observacoes: '',
    cliente_id: '',
    cliente_nome: '',
  });

  const company = JSON.parse(localStorage.getItem('company') || '{}');

  useEffect(() => {
    fetchCategorias();
    fetchClientes();
    fetchContas();
    fetchContadoresNota();
    fetchBancosAtivos();
  }, [selectedMonth, filterStatus, filterCategoria, filterCliente]);

  const fetchBancosAtivos = async () => {
    if (!company.id) return;
    try {
      const response = await axiosInstance.get(`/contabil/contas-bancarias/${company.id}`);
      setBancosAtivos((response.data.contas_bancarias || []).filter((b) => b.ativo));
    } catch (error) {
      // FASE 4a é opcional — sem contas bancárias cadastradas, segue o fluxo de sempre (1.1.01)
    }
  };

  const fetchContadoresNota = async () => {
    if (!company.id) return;
    try {
      const response = await axiosInstance.get(`/contadores/${company.id}`, { params: { ativo: true } });
      setContadoresNota(response.data);
    } catch (error) {
      console.error('Erro ao carregar contadores:', error);
    }
  };

  const fetchNotaVinculada = async (contaId) => {
    if (!company.id || !contaId) {
      setNotaVinculadaExistente(null);
      return;
    }
    try {
      const response = await axiosInstance.get('/notas-venda', {
        params: { empresa_id: company.id, conta_id: contaId },
      });
      setNotaVinculadaExistente(response.data?.[0] || null);
    } catch {
      setNotaVinculadaExistente(null);
    }
  };

  const fetchCategorias = async () => {
    try {
      const response = await axiosInstance.get('/contas/categorias');
      setCategorias((response.data.receber || []).slice().sort((a, b) => a.localeCompare(b, 'pt-BR', { sensitivity: 'base' })));
    } catch (error) {
      toast.error('Erro ao carregar categorias');
    }
  };

  const fetchClientes = async () => {
    if (!company.id) return;
    try {
      const response = await axiosInstance.get(`/clientes/${company.id}`);
      setClientes(response.data);
    } catch (error) {
      toast.error('Erro ao carregar clientes');
    }
  };

  const handleClienteSaved = (cliente) => {
    setClientes((prev) => [...prev, cliente]);
    setFormData((prev) => ({ ...prev, cliente_id: cliente.id, cliente_nome: getClienteDisplayName(cliente) }));
  };

  const fetchContas = async () => {
    if (!company.id) return;

    try {
      let url = `/contas/receber?company_id=${company.id}`;
      // Ao filtrar por cliente, mostra todas as ocorrências independente do mês
      if (selectedMonth && filterCliente === 'all') url += `&mes=${selectedMonth}`;
      if (filterStatus !== 'all') url += `&status=${filterStatus}`;
      if (filterCategoria !== 'all') url += `&categoria=${filterCategoria}`;
      if (filterCliente !== 'all') url += `&cliente_id=${encodeURIComponent(filterCliente)}`;

      const response = await axiosInstance.get(url);
      setContas(response.data);
      setFilteredContas(response.data);
    } catch (error) {
      toast.error('Erro ao carregar contas a receber');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.categoria) {
      toast.error('Categoria é obrigatória.');
      return;
    }

    if (!formData.cliente_id && !formData.descricao.trim()) {
      toast.error('Selecione um cliente ou informe a descrição da conta');
      return;
    }

    setLoading(true);

    try {
      const data = {
        company_id: company.id,
        user_id: (user?.id || user?.user_id),
        tipo: 'RECEBER',
        ...formData,
        valor: parseCurrencyToNumber(formData.valor),
      };

      if (editingConta) {
        await axiosInstance.put(`/contas/receber/${editingConta.id}`, data);
        toast.success('Conta atualizada!');
        setShowDialog(false);
        resetForm();
        fetchContas();
      } else {
        const res = await axiosInstance.post('/contas/receber', data);
        if (repetirConta && numRepeticoes > 0) {
          const repeticoes = Array.from({ length: numRepeticoes }, (_, i) =>
            axiosInstance.post('/contas/receber', {
              ...data,
              data_emissao: addMonths(data.data_emissao, i + 1),
              data_vencimento: addMonths(data.data_vencimento, i + 1),
            })
          );
          await Promise.all(repeticoes);
          toast.success(`Conta criada com ${numRepeticoes} repetição(ões)! Você pode anexar documentos à conta principal.`);
        } else {
          toast.success('Conta criada! Agora você pode anexar documentos.');
        }
        const novaConta = { ...data, id: res.data.conta_id };
        setEditingConta(novaConta);
        setNotaVinculadaExistente(null);
        fetchDocumentos(res.data.conta_id);
        fetchContas();
      }
    } catch (error) {
      toast.error('Erro ao salvar conta');
    } finally {
      setLoading(false);
    }
  };

  const fetchDocumentos = async (conta_id) => {
    try {
      const res = await axiosInstance.get(`/contas/receber/${conta_id}/documentos`);
      setDocumentos(res.data);
    } catch {
      setDocumentos([]);
    }
  };

  const handleUploadDoc = async (e) => {
    const file = e.target.files[0];
    if (!file || !editingConta) return;
    if (enviarContador && (!notaUnidade || !notaContadorId)) {
      toast.error('Selecione a Empresa e o Contador para enviar ao Contador, ou desmarque a opção "Enviar ao Contador".');
      e.target.value = '';
      return;
    }
    setUploadingDoc(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axiosInstance.post(`/contas/receber/${editingConta.id}/documentos`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await fetchDocumentos(editingConta.id);
      toast.success('Documento anexado!');

      if (enviarContador && notaUnidade && notaContadorId && editingConta.cliente_id) {
        try {
          const fdNota = new FormData();
          fdNota.append('empresa_id', company.id);
          fdNota.append('unidade', notaUnidade);
          fdNota.append('contador_id', notaContadorId);
          fdNota.append('mes_referencia', (editingConta.data_emissao || '').slice(0, 7));
          fdNota.append('valor', editingConta.valor);
          fdNota.append('cliente_id', editingConta.cliente_id);
          fdNota.append('cliente_nome', editingConta.cliente_nome || '');
          fdNota.append('conta_receber_vinculada_id', editingConta.id);
          fdNota.append('arquivos', file);
          await axiosInstance.post('/notas-venda/upload-lote', fdNota, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          toast.success('Documento enviado também para o Contador (Notas de Venda)!');
          fetchNotaVinculada(editingConta.id);
        } catch (errNota) {
          toast.error(errNota.response?.data?.detail || 'Documento anexado, mas falhou o envio ao Contador');
        }
      }
    } catch {
      toast.error('Erro ao anexar documento');
    } finally {
      setUploadingDoc(false);
      e.target.value = '';
    }
  };

  const handleDeleteDoc = async (filename) => {
    if (!window.confirm(`Excluir "${filename}"?`)) return;
    try {
      await axiosInstance.delete(`/contas/receber/${editingConta.id}/documentos/${encodeURIComponent(filename)}`);
      setDocumentos((prev) => prev.filter((d) => d.filename !== filename));
      toast.success('Documento excluído');
    } catch {
      toast.error('Erro ao excluir documento');
    }
  };

  const handleViewDoc = async (filename) => {
    try {
      const res = await axiosInstance.get(
        `/contas/receber/${editingConta.id}/documentos/${encodeURIComponent(filename)}`,
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank');
    } catch {
      toast.error('Erro ao abrir documento');
    }
  };

  const handleViewDocConta = async (conta) => {
    try {
      const res = await axiosInstance.get(`/contas/receber/${conta.id}/documentos`);
      const docs = res.data;
      if (!docs || docs.length === 0) {
        toast.error('Nenhum documento anexado a esta conta');
        return;
      }
      const fileRes = await axiosInstance.get(
        `/contas/receber/${conta.id}/documentos/${encodeURIComponent(docs[0].filename)}`,
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(fileRes.data);
      window.open(url, '_blank');
    } catch {
      toast.error('Erro ao abrir documento');
    }
  };

  const handleEdit = (conta) => {
    setEditingConta(conta);
    setDocumentos([]);
    setEnviarContador(false);
    setNotaUnidade('');
    setNotaContadorId('');
    fetchDocumentos(conta.id);
    fetchNotaVinculada(conta.id);
    setFormData({
      descricao: conta.descricao,
      categoria: conta.categoria,
      data_emissao: conta.data_emissao,
      data_vencimento: conta.data_vencimento,
      valor: conta.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      forma_pagamento: conta.forma_pagamento,
      observacoes: conta.observacoes || '',
      cliente_id: conta.cliente_id || '',
      cliente_nome: conta.cliente_nome || '',
    });
    setShowDialog(true);
  };

  const handleDuplicate = (conta) => {
    setEditingConta(null);
    setRepetirConta(false);
    setNumRepeticoes(1);
    setDocumentos([]);
    setFormData({
      descricao: conta.descricao,
      categoria: conta.categoria,
      data_emissao: conta.data_emissao,
      data_vencimento: conta.data_vencimento,
      valor: conta.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      forma_pagamento: conta.forma_pagamento,
      observacoes: conta.observacoes || '',
      cliente_id: conta.cliente_id || '',
      cliente_nome: conta.cliente_nome || '',
    });
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deseja realmente excluir esta conta?')) return;

    try {
      await axiosInstance.delete(`/contas/receber/${id}`);
      toast.success('Conta excluída!');
      fetchContas();
    } catch (error) {
      toast.error('Erro ao excluir conta');
    }
  };

  const handleMarcarRecebido = async (conta) => {
    if (bancosAtivos.length > 0) {
      setBancoSelecionadoRecebimento('');
      setContaParaReceber(conta);
      return;
    }
    if (!window.confirm('Confirma marcar esta conta como recebida?')) return;
    try {
      await axiosInstance.patch(`/contas/receber/${conta.id}/status`, {
        status: 'RECEBIDO',
        data_pagamento: new Date().toISOString().slice(0, 10),
      });
      toast.success('Conta marcada como recebida!');
      fetchContas();
    } catch (error) {
      toast.error('Erro ao marcar como recebida');
    }
  };

  const handleConfirmarRecebimentoComBanco = async () => {
    if (!contaParaReceber) return;
    try {
      await axiosInstance.patch(`/contas/receber/${contaParaReceber.id}/status`, {
        status: 'RECEBIDO',
        data_pagamento: new Date().toISOString().slice(0, 10),
        conta_bancaria_id: bancoSelecionadoRecebimento || null,
      });
      toast.success('Conta marcada como recebida!');
      setContaParaReceber(null);
      fetchContas();
    } catch (error) {
      toast.error('Erro ao marcar como recebida');
    }
  };

  const handleReverterRecebido = async (conta) => {
    if (!window.confirm('Confirma reverter esta conta para PENDENTE? O lançamento financeiro gerado pela quitação será cancelado.')) return;
    try {
      await axiosInstance.patch(`/contas/receber/${conta.id}/status`, {
        status: 'PENDENTE',
      });
      toast.success('Conta revertida para pendente!');
      fetchContas();
    } catch (error) {
      toast.error('Erro ao reverter conta');
    }
  };

  const handleMarcarRecebidoEmLote = async () => {
    if (selectedContas.length === 0) {
      toast.error('Selecione pelo menos uma conta');
      return;
    }

    if (!window.confirm(`Confirma marcar ${selectedContas.length} conta(s) como recebida(s)?`)) return;

    try {
      const promises = selectedContas.map((id) =>
        axiosInstance.patch(`/contas/receber/${id}/status`, {
          status: 'RECEBIDO',
          data_pagamento: new Date().toISOString().slice(0, 10),
        })
      );

      await Promise.all(promises);
      toast.success(`${selectedContas.length} conta(s) marcada(s) como recebida(s)!`);
      setSelectedContas([]);
      fetchContas();
    } catch (error) {
      toast.error('Erro ao marcar contas como recebidas');
    }
  };

  const resetForm = () => {
    setFormData({
      descricao: '',
      categoria: '',
      data_emissao: new Date().toISOString().slice(0, 10),
      data_vencimento: new Date().toISOString().slice(0, 10),
      valor: '',
      forma_pagamento: 'PIX',
      observacoes: '',
      cliente_id: '',
      cliente_nome: '',
    });
    setEditingConta(null);
    setRepetirConta(false);
    setNumRepeticoes(1);
    setDocumentos([]);
    setEnviarContador(false);
    setNotaUnidade('');
    setNotaContadorId('');
    setNotaVinculadaExistente(null);
  };

  const getStatusBadge = (status) => {
    const variants = {
      PENDENTE: 'default',
      RECEBIDO: 'success',
      PAGO: 'success',
      ATRASADO: 'destructive',
      PARCIAL: 'warning',
    };

    const icons = {
      PENDENTE: <AlertCircle className="w-3 h-3 mr-1" />,
      RECEBIDO: <CheckCircle className="w-3 h-3 mr-1" />,
      PAGO: <CheckCircle className="w-3 h-3 mr-1" />,
      ATRASADO: <XCircle className="w-3 h-3 mr-1" />,
      PARCIAL: <AlertCircle className="w-3 h-3 mr-1" />,
    };

    const labels = {
      PENDENTE: 'A vencer',
      RECEBIDO: 'Recebido',
      PAGO: 'Pago',
      ATRASADO: 'Atrasada',
      PARCIAL: 'Parcial',
    };

    return (
      <Badge variant={variants[status] || 'default'} className="flex items-center w-fit">
        {icons[status]}
        {labels[status] || status}
      </Badge>
    );
  };

  const getValorColorClass = (status) => {
    if (status === 'RECEBIDO' || status === 'PAGO') return 'text-green-500';
    if (status === 'ATRASADO') return 'text-red-500';
    return 'text-blue-500';
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      const pendentes = filteredContas
        .filter((c) => c.status === 'PENDENTE' || c.status === 'ATRASADO')
        .map((c) => c.id);
      setSelectedContas(pendentes);
    } else {
      setSelectedContas([]);
    }
  };

  const handleSelectConta = (id, checked) => {
    if (checked) {
      setSelectedContas([...selectedContas, id]);
    } else {
      setSelectedContas(selectedContas.filter((cid) => cid !== id));
    }
  };

  const totalPendente = filteredContas
    .filter((c) => c.status === 'PENDENTE' || c.status === 'ATRASADO')
    .reduce((sum, c) => sum + c.valor, 0);

  const totalRecebido = filteredContas
    .filter((c) => c.status === 'RECEBIDO' || c.status === 'PAGO')
    .reduce((sum, c) => sum + c.valor, 0);

  return (
    <div className="flex min-h-screen bg-zinc-950 text-white">
      <Sidebar user={user} onLogout={onLogout} activePage="contas-receber" />

      <div className="flex-1 p-8">
        <div className="space-y-6">
          <SubscriptionCard user={user} />

          {/* Header */}
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold">Contas a Receber</h1>
              <p className="text-zinc-400 mt-1">Gerencie seus recebimentos e contratos</p>
            </div>
            <Button
              onClick={() => {
                resetForm();
                setShowDialog(true);
              }}
              className="bg-green-600 hover:bg-green-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nova Conta a Receber
            </Button>
          </div>

          {/* KPIs */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-sm font-medium text-zinc-400">Pendente</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-yellow-500">
                  R$ {totalPendente.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </div>
                <p className="text-xs text-zinc-500 mt-1">
                  {filteredContas.filter((c) => c.status === 'PENDENTE' || c.status === 'ATRASADO').length} conta(s)
                </p>
              </CardContent>
            </Card>

            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-sm font-medium text-zinc-400">Recebido</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-500">
                  R$ {totalRecebido.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </div>
                <p className="text-xs text-zinc-500 mt-1">
                  {filteredContas.filter((c) => c.status === 'RECEBIDO' || c.status === 'PAGO').length} conta(s)
                </p>
              </CardContent>
            </Card>

            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-sm font-medium text-zinc-400">Total</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  R$ {(totalPendente + totalRecebido).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </div>
                <p className="text-xs text-zinc-500 mt-1">{filteredContas.length} conta(s)</p>
              </CardContent>
            </Card>
          </div>

          {/* Filtros */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="pt-6">
              <div className="flex flex-wrap gap-4">
                <div className="flex-1 min-w-[200px]">
                  <Label>Mês</Label>
                  <Input
                    type="month"
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>

                <div className="flex-1 min-w-[200px]">
                  <Label>Status</Label>
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="PENDENTE">A vencer</SelectItem>
                      <SelectItem value="RECEBIDO">Recebido</SelectItem>
                      <SelectItem value="ATRASADO">Atrasada</SelectItem>
                      <SelectItem value="PARCIAL">Parcial</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex-1 min-w-[200px]">
                  <Label>Categoria</Label>
                  <Select value={filterCategoria} onValueChange={setFilterCategoria}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas</SelectItem>
                      {categorias.map((cat) => (
                        <SelectItem key={cat} value={cat}>
                          {cat}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex-1 min-w-[200px]">
                  <Label>Cliente</Label>
                  <Select value={filterCliente} onValueChange={setFilterCliente}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {clientes.map((cliente) => (
                        <SelectItem key={cliente.id} value={cliente.id}>
                          {getClienteDisplayName(cliente)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {selectedContas.length > 0 && (
                <div className="mt-4 flex items-center gap-4">
                  <span className="text-sm text-zinc-400">
                    {selectedContas.length} conta(s) selecionada(s)
                  </span>
                  <Button onClick={handleMarcarRecebidoEmLote} size="sm" className="bg-green-600 hover:bg-green-700">
                    Marcar como Recebido
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Tabela */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="pt-6">
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-800">
                    <TableHead className="w-[50px]">
                      <Checkbox
                        checked={selectedContas.length > 0}
                        onCheckedChange={handleSelectAll}
                      />
                    </TableHead>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Descrição</TableHead>
                    <TableHead>Categoria</TableHead>
                    <TableHead>Vencimento</TableHead>
                    <TableHead>Valor</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Pagamento</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredContas.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center text-zinc-500 py-8">
                        Nenhuma conta encontrada
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredContas.map((conta) => (
                      <TableRow key={conta.id} className="border-zinc-800">
                        <TableCell>
                          {(conta.status === 'PENDENTE' || conta.status === 'ATRASADO') && (
                            <Checkbox
                              checked={selectedContas.includes(conta.id)}
                              onCheckedChange={(checked) => handleSelectConta(conta.id, checked)}
                            />
                          )}
                        </TableCell>
                        <TableCell className="text-zinc-300">{conta.cliente_nome || '—'}</TableCell>
                        <TableCell className="font-medium">{conta.descricao || '—'}</TableCell>
                        <TableCell>{conta.categoria}</TableCell>
                        <TableCell>
                          {new Date(conta.data_vencimento + 'T00:00:00').toLocaleDateString('pt-BR')}
                        </TableCell>
                        <TableCell className={`font-medium ${getValorColorClass(conta.status)}`}>
                          R$ {conta.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell>{getStatusBadge(conta.status)}</TableCell>
                        <TableCell>{conta.forma_pagamento}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            {conta.status !== 'RECEBIDO' && conta.status !== 'PAGO' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleMarcarRecebido(conta)}
                                className="border-green-600 text-green-600 hover:bg-green-600 hover:text-white"
                                title="Quitar"
                              >
                                <CheckCircle className="w-4 h-4" />
                              </Button>
                            )}
                            {(conta.status === 'RECEBIDO' || conta.status === 'PAGO') && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleReverterRecebido(conta)}
                                className="border-yellow-600 text-yellow-600 hover:bg-yellow-600 hover:text-white"
                                title="Reverter para pendente"
                              >
                                <RotateCcw className="w-4 h-4" />
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDuplicate(conta)}
                              className="border-zinc-700 text-zinc-400 hover:text-white"
                              title="Duplicar conta"
                            >
                              <CopyPlus className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleViewDocConta(conta)}
                              className="border-zinc-700 text-zinc-400 hover:text-white"
                              title="Visualizar documento"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEdit(conta)}
                              className="border-zinc-700"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDelete(conta.id)}
                              className="border-red-600 text-red-600 hover:bg-red-600 hover:text-white"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Dialog de Criar/Editar */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingConta ? 'Editar Conta' : 'Nova Conta a Receber'}</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Cliente</Label>
              <div className="flex gap-2">
                <Popover open={clienteComboOpen} onOpenChange={setClienteComboOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      role="combobox"
                      aria-expanded={clienteComboOpen}
                      className="flex-1 justify-between bg-zinc-800 border-zinc-700 font-normal hover:bg-zinc-800"
                    >
                      <span className={cn(!formData.cliente_nome && 'text-zinc-500')}>
                        {formData.cliente_nome || 'Buscar cliente pelo nome...'}
                      </span>
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[--radix-popover-trigger-width] p-0 bg-zinc-800 border-zinc-700">
                    <Command className="bg-zinc-800">
                      <CommandInput
                        placeholder="Digite qualquer parte do nome..."
                        value={clienteSearch}
                        onValueChange={setClienteSearch}
                      />
                      <CommandList>
                        <CommandEmpty>
                          <div className="flex flex-col items-center gap-2 px-2">
                            <span>Nenhum cliente encontrado</span>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="border-zinc-600"
                              onClick={() => {
                                setClienteComboOpen(false);
                                setShowNovoClienteDialog(true);
                              }}
                            >
                              Cadastrar novo cliente
                            </Button>
                          </div>
                        </CommandEmpty>
                        <CommandGroup>
                          {formData.cliente_id && (
                            <CommandItem
                              onSelect={() => {
                                setFormData({ ...formData, cliente_id: '', cliente_nome: '' });
                                setClienteComboOpen(false);
                              }}
                              className="text-zinc-400"
                            >
                              Limpar seleção
                            </CommandItem>
                          )}
                          {clientes.map((cliente) => {
                            const nome = getClienteDisplayName(cliente);
                            return (
                              <CommandItem
                                key={cliente.id}
                                value={nome}
                                onSelect={() => {
                                  setFormData({ ...formData, cliente_id: cliente.id, cliente_nome: nome });
                                  setClienteComboOpen(false);
                                }}
                              >
                                <Check
                                  className={cn(
                                    'mr-2 h-4 w-4',
                                    formData.cliente_id === cliente.id ? 'opacity-100' : 'opacity-0'
                                  )}
                                />
                                {nome}
                              </CommandItem>
                            );
                          })}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => setShowNovoClienteDialog(true)}
                  className="border-zinc-700 text-zinc-300 hover:bg-zinc-700 shrink-0"
                  title="Cadastrar novo cliente"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div>
              <Label>Descrição {!formData.cliente_id && '*'}</Label>
              <Input
                required={!formData.cliente_id}
                value={formData.descricao}
                onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                placeholder="Ex: Pagamento do cliente X"
                className="bg-zinc-800 border-zinc-700"
              />
              <p className="text-xs text-zinc-500 mt-1">
                Selecione um cliente acima ou preencha a descrição da conta.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Categoria *</Label>
                <Select
                  required
                  value={formData.categoria}
                  onValueChange={(value) => setFormData({ ...formData, categoria: value })}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700">
                    <SelectValue placeholder="Selecione..." />
                  </SelectTrigger>
                  <SelectContent>
                    {categorias.map((cat) => (
                      <SelectItem key={cat} value={cat}>
                        {cat}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Valor *</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">R$</span>
                  <Input
                    required
                    type="text"
                    inputMode="numeric"
                    value={formData.valor}
                    onChange={(e) => setFormData({ ...formData, valor: formatCurrencyInput(e.target.value) })}
                    placeholder="0,00"
                    className="bg-zinc-800 border-zinc-700 pl-10"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Data de Emissão *</Label>
                <Input
                  required
                  type="date"
                  value={formData.data_emissao}
                  onChange={(e) => setFormData({ ...formData, data_emissao: e.target.value })}
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>

              <div>
                <Label>Data de Vencimento *</Label>
                <Input
                  required
                  type="date"
                  value={formData.data_vencimento}
                  onChange={(e) => setFormData({ ...formData, data_vencimento: e.target.value })}
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>
            </div>

            <div>
              <Label>Forma de Recebimento *</Label>
              <Select
                required
                value={formData.forma_pagamento}
                onValueChange={(value) => setFormData({ ...formData, forma_pagamento: value })}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PIX">PIX</SelectItem>
                  <SelectItem value="Boleto">Boleto</SelectItem>
                  <SelectItem value="Cartão">Cartão</SelectItem>
                  <SelectItem value="Dinheiro">Dinheiro</SelectItem>
                  <SelectItem value="Transferência">Transferência</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Observações</Label>
              <Textarea
                value={formData.observacoes}
                onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                placeholder="Observações adicionais..."
                className="bg-zinc-800 border-zinc-700"
                rows={3}
              />
            </div>

            {editingConta && (
              <div className="border border-zinc-700 rounded-md p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2">
                    <Paperclip className="w-4 h-4" />
                    Documentos Anexados
                  </Label>
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      className="hidden"
                      onChange={handleUploadDoc}
                      disabled={uploadingDoc}
                    />
                    <span className="inline-flex items-center gap-1 text-sm px-3 py-1 rounded-md border border-zinc-600 text-zinc-300 hover:bg-zinc-700 transition-colors">
                      {uploadingDoc ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                      Anexar
                    </span>
                  </label>
                </div>
                {documentos.length === 0 ? (
                  <p className="text-xs text-zinc-500">Nenhum documento anexado</p>
                ) : (
                  <ul className="space-y-2">
                    {documentos.map((doc) => (
                      <li key={doc.filename} className="flex items-center justify-between bg-zinc-800 rounded px-3 py-2">
                        <span className="text-sm text-zinc-300 truncate flex-1 mr-2">{doc.filename}</span>
                        <div className="flex gap-1 shrink-0">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="border-zinc-600 px-2 h-7"
                            onClick={() => handleViewDoc(doc.filename)}
                            title="Visualizar"
                          >
                            <Eye className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="border-red-700 text-red-500 hover:bg-red-700 hover:text-white px-2 h-7"
                            onClick={() => handleDeleteDoc(doc.filename)}
                            title="Excluir"
                          >
                            <X className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {editingConta && (
              <div className="border border-zinc-700 rounded-md p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="enviar-contador"
                    checked={enviarContador}
                    onCheckedChange={setEnviarContador}
                    disabled={!editingConta.cliente_id || !!notaVinculadaExistente}
                  />
                  <Label htmlFor="enviar-contador" className="cursor-pointer flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Enviar este documento também para o Contador (Notas de Venda)
                  </Label>
                </div>
                {!editingConta.cliente_id && (
                  <p className="text-xs text-amber-500">
                    Selecione um Cliente nesta conta para poder enviar o documento ao Contador.
                  </p>
                )}
                {notaVinculadaExistente && (
                  <p className="text-xs text-amber-500">
                    Esta conta já tem uma Nota de Venda vinculada (mês de referência {notaVinculadaExistente.mes_referencia}) — não é possível vincular duas vezes.
                  </p>
                )}
                {enviarContador && editingConta.cliente_id && !notaVinculadaExistente && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Empresa *</Label>
                      <Select
                        value={notaUnidade}
                        onValueChange={(v) => { setNotaUnidade(v); setNotaContadorId(''); }}
                      >
                        <SelectTrigger className="bg-zinc-800 border-zinc-700">
                          <SelectValue placeholder="Selecione" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="matriz">Matriz</SelectItem>
                          <SelectItem value="filial">Filial</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Contador *</Label>
                      <Select
                        value={notaContadorId}
                        onValueChange={setNotaContadorId}
                        disabled={!notaUnidade}
                      >
                        <SelectTrigger className="bg-zinc-800 border-zinc-700">
                          <SelectValue placeholder={notaUnidade ? 'Selecione' : 'Selecione a empresa primeiro'} />
                        </SelectTrigger>
                        <SelectContent>
                          {contadoresNota.filter((c) => c.empresas_atendidas.includes(notaUnidade)).map((c) => (
                            <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <p className="text-xs text-zinc-500 col-span-2">
                      Use o botão "Anexar" no card "Documentos Anexados" acima para enviar o PDF — ele será salvo aqui e também nas Notas de Venda do contador selecionado.
                    </p>
                  </div>
                )}
              </div>
            )}

            {!editingConta && (
              <div className="border border-zinc-700 rounded-md p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="repetir-conta"
                    checked={repetirConta}
                    onCheckedChange={setRepetirConta}
                  />
                  <Label htmlFor="repetir-conta" className="cursor-pointer">
                    Repetir esta conta mensalmente
                  </Label>
                </div>
                {repetirConta && (
                  <div>
                    <Label>Número de repetições</Label>
                    <Input
                      type="number"
                      min={1}
                      max={60}
                      value={numRepeticoes}
                      onChange={(e) => setNumRepeticoes(Math.max(1, parseInt(e.target.value) || 1))}
                      className="bg-zinc-800 border-zinc-700 w-32 mt-1"
                    />
                    <p className="text-xs text-zinc-500 mt-1">
                      Serão criadas mais {numRepeticoes} cópia(s) com vencimento nos meses seguintes, no mesmo dia.
                    </p>
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-between pt-4">
              {editingConta ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleDuplicate(editingConta)}
                  className="border-zinc-700 text-zinc-400 hover:text-white"
                >
                  <CopyPlus className="w-4 h-4 mr-2" />
                  Duplicar
                </Button>
              ) : (
                <span />
              )}
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowDialog(false);
                    resetForm();
                  }}
                  className="border-zinc-700"
                >
                  Cancelar
                </Button>
                <Button type="submit" disabled={loading} className="bg-green-600 hover:bg-green-700">
                  {loading ? 'Salvando...' : editingConta ? 'Atualizar' : 'Criar'}
                </Button>
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <ClienteFormDialog
        open={showNovoClienteDialog}
        onOpenChange={setShowNovoClienteDialog}
        cliente={null}
        companyId={company.id}
        initialNome={clienteSearch}
        onSaved={handleClienteSaved}
      />

      {/* Confirmar Recebimento — seletor de conta bancária (FASE 4a) */}
      <Dialog open={!!contaParaReceber} onOpenChange={(open) => !open && setContaParaReceber(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle>Confirmar Recebimento</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-zinc-400">{contaParaReceber?.cliente_nome || contaParaReceber?.descricao}</p>
            <div>
              <Label>Em qual conta caiu o recebimento?</Label>
              <Select value={bancoSelecionadoRecebimento} onValueChange={setBancoSelecionadoRecebimento}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700">
                  <SelectValue placeholder="Não informar (Caixa e Bancos)" />
                </SelectTrigger>
                <SelectContent>
                  {bancosAtivos.map((b) => (
                    <SelectItem key={b.id} value={b.id}>{b.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="gap-2 mt-4">
            <Button type="button" variant="outline" onClick={() => setContaParaReceber(null)} className="border-zinc-700">Cancelar</Button>
            <Button type="button" onClick={handleConfirmarRecebimentoComBanco} className="bg-green-600 hover:bg-green-700">Confirmar Recebimento</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContasReceber;
